import Handlebars from 'handlebars';
import { postJson, fetchJson } from '../../core/ajax-json.js';
import uploadDocuments from '../../upload-documents.js';
import { fetchBalance, fetchFeesAndLimits, isKycNeeded } from '../withdraw-services.js';
import kycPopup from './kyc-popup.js';
import { spinnerOff, childSpinnerOnAction, siblingSpinnerOnAction } from '../../flow/flow-view.js';
import * as validation from './validation.js';
import * as validators from './validators.js';
import * as uiFeedback from './validation-ui-feedback.js';
import notifications from './../../notifications.js';
import currencyFormatter from './../../libs/currency-formatter.js';
import { getLocale } from './../../libs/locale.js';


// view helpers
const amountTemplate = {
	template: undefined,
	rootContext: {}
};
const viewIds = {
	amount: undefined,
	form: undefined,
	feeContextElement: undefined,
	limitContextElement: undefined
};
function getContextBasedId(contextPath, id) {
	return `${contextPath}-${id}`;
}
function getWithdrawFormId() {
	return viewIds.form;
}
function getFeeElementId() {
	return viewIds.feeContextElement;
}
function getLimitElementId() {
	return viewIds.limitContextElement;
}
function getWithdrawAmountFieldId() {
	return viewIds.amount;
}
function setViewIds(contextPath) {
	viewIds.form = getContextBasedId(contextPath, 'withdraw-form-id');
	viewIds.amount = getContextBasedId(contextPath, 'withdraw-amount-field-id');
	viewIds.feeContextElement = getContextBasedId(contextPath, 'withdraw-fee-context-element');
	viewIds.limitContextElement = getContextBasedId(contextPath, 'withdraw-limit-context-element');
}


function templateDataWithAmountValue(rootContext, feeLimitData, balanceData) {
	const bonusFree = parseFloat(balanceData.PARAM_BONUS_FREE_MONEY_BALANCE);
	const bonusLocked = parseFloat(balanceData.PARAM_BONUS_LOCKED_MONEY_BALANCE);

	const newContext = {};
	const rootContextKeys = Object.keys(rootContext);
	rootContextKeys.forEach(key => {
		newContext[key] = rootContext[key];
	});

	newContext.PARAM_AMOUNT = parseFloat(feeLimitData.PARAM_AMOUNT);
	newContext.PARAM_TOTAL_AMOUNT = parseFloat(feeLimitData.PARAM_TOTAL_AMOUNT);
	newContext.PARAM_FEE_AMOUNT = parseFloat(feeLimitData.PARAM_FEE_AMOUNT);
	newContext.SELECTED_CURRENCY = feeLimitData.SELECTED_CURRENCY;
	newContext.PARAM_MIN_OPERATION = parseFloat(feeLimitData.PARAM_MIN_OPERATION);
	newContext.PARAM_MAX_OPERATION = parseFloat(feeLimitData.PARAM_MAX_OPERATION);
	newContext.PARAM_BALANCE_TOTAL = parseFloat(balanceData.PARAM_BALANCE_TOTAL);
	newContext.PARAM_BONUS_BALANCE = bonusFree + bonusLocked;
	newContext.PARAM_AVILABLE_TO_WITHDRAW = newContext.PARAM_BALANCE_TOTAL - newContext.PARAM_BONUS_BALANCE - newContext.PARAM_FEE_AMOUNT;

	if (newContext.PARAM_AVILABLE_TO_WITHDRAW < 0) {
		newContext.PARAM_AVILABLE_TO_WITHDRAW = 0;
	}
	newContext.PARAM_MAX_OPERATION = Math.min(newContext.PARAM_AVILABLE_TO_WITHDRAW, newContext.PARAM_MAX_OPERATION);
	rootContext.SELECTED_CURRENCY = feeLimitData.SELECTED_CURRENCY;
	return newContext;
}
function createFeeLimitVO(withdrawAmount, currency, methodData, accountData) {
	return {
		paymentMethodId: methodData.methodId,
		amount: withdrawAmount,
		aid: accountData && accountData.id,
		isMobileDevice: true,
		currency
	};
}
// js-payment__input-cvc-creditcard payment__input-cvc-creditcard input-wrap__input
const formValidation = {
	amountForm: {
		amountMin: undefined,
		amountMax: undefined,

		am(value) {
			console.log(amountTemplate);
			if (validators.isBetween(parseFloat(value), this.amountMin, this.amountMax)) {
				return {
					inRange: true
				};
			}
			return {
				inRange: false,
				isGreater: validators.isGreater(value, this.amountMax)
			};
		}
	},
	setValidationConstraints(validationConstraints) {
		this.amountForm.amountMax = validationConstraints.PARAM_MAX_OPERATION;
		this.amountForm.amountMin = validationConstraints.PARAM_MIN_OPERATION;
	},
	onInput(formElement) {
		const validationResult = validation.validateFormElements(formElement, this.amountForm);
		uiFeedBackForMinMax(validationResult, formElement, this.amountForm.amountMin, this.amountForm.amountMax);
		return validationResult;
	},
	onSubmit(formElement) {
		const validationResult = validation.validateFormElements(formElement, this.amountForm);
		uiFeedBackForMinMax(validationResult, formElement, this.amountForm.amountMin, this.amountForm.amountMax);
		return validationResult;
	}
};

/***
 * handlers
 */
function feeLimitUpdate(sharedPaymentMethodStore, formElement) {
	const amountElement = formElement.am;
	const parsedFloat = parseFloat(amountElement.value);
	const withdrawAmount = isNaN(parsedFloat) ? 0 : parsedFloat;

	sharedPaymentMethodStore.setLastUsedAmount(withdrawAmount);

	const methodData = sharedPaymentMethodStore.getMethod();
	const accountData = sharedPaymentMethodStore.getAccount();
	const currency = sharedPaymentMethodStore.getCurrency();
	const feeLimitVO = createFeeLimitVO(withdrawAmount, currency, methodData, accountData);
	return Promise.all([fetchBalance(), fetchFeesAndLimits(feeLimitVO)])
		.then(([balanceData, feeLimitData]) => {
			const rootContext = templateDataWithAmountValue(amountTemplate.rootContext, feeLimitData, balanceData);
			feeLimitTemplateUpdate(formElement, rootContext);
			formValidation.setValidationConstraints(extractValidationConstraints(rootContext));
		});
}
function feeLimitTemplateUpdate(formElement, templateRootContext) {
	const feeElement = formElement.ownerDocument.getElementById(getFeeElementId());
	feeElement.innerHTML = amountTemplate.feeTemplate(templateRootContext);
}
function extractValidationConstraints(feeLimitData) {
	return {
		PARAM_MAX_OPERATION: feeLimitData.PARAM_MAX_OPERATION,
		PARAM_MIN_OPERATION: feeLimitData.PARAM_MIN_OPERATION,
	};
}

function amountFormChange(sharedPaymentMethodStore, formElement) {
	feeLimitUpdate(sharedPaymentMethodStore, formElement)
		.then(() => {
			formValidation.onInput(formElement);
		});
}

/* Only entries explicitly listed in adapters will be used to serialize data from dataArray.
 * Overwrites on key collision
 */
function applyAdapters(adapters, dataArray) {
	if (adapters.length !== dataArray.length) {
		throw new Error('Adapter and data size mismatch when building withdraw request');
	}

	const data = { isMobileDevice: true };
	for (let i = 0; i < adapters.length; i += 1) {
		const adapter = adapters[i];
		const inData = dataArray[i];

		Object.keys(inData)
			.forEach(key => {
				if (adapter[key]) {
					data[adapter[key]] = inData[key];
				}
			});
	}
	return data;
}
function creditCardWithdrawDataAdapter(method, account) {
	const accountSerializationKeys = {
		id: 'cid',
	};
	const adapterInfo = [accountSerializationKeys];
	const dataToSerialize = [account];

	return applyAdapters(adapterInfo, dataToSerialize);
}
function trustlyAdapter(method, account) {
	const methodSerializationKeys = {
		methodId: 'paymentMethodId',
	};
	const adapterInfo = [methodSerializationKeys];
	const dataToSerialize = [method];

	return applyAdapters(adapterInfo, dataToSerialize);
}
function piqWithdrawDataAdapter(method, account) {
	const methodSerializationKeys = {
		methodId: 'paymentMethodId',
	};
	const accountSerializationKeys = {
		accountNumber: 'nu',
		clearingNumber: 'so',
		country: 'cid1'
	};
	const adapterInfo = [methodSerializationKeys, accountSerializationKeys];
	const dataToSerialize = [method, account];

	return applyAdapters(adapterInfo, dataToSerialize);
}
function withdrawDataUsingAdapter(method, account) {
	const adapters = {
		1: creditCardWithdrawDataAdapter,
		23: trustlyAdapter,
		57: piqWithdrawDataAdapter,
	};

	return adapters[method.methodId](method, account);
}

function showKycPopup(kycResponse, contextElement, viewDoneResolver) {
	kycPopup.show(kycResponse, viewDoneResolver, () => {
		let viewReadyResolver;
		const viewReadyPromise = new Promise((resolve) => {
			viewReadyResolver = resolve;
		});

		uploadDocuments.view(viewReadyPromise)
			.then((uploadDocument) => {
				contextElement.innerHTML = '';
				const wrapperElement = document.createElement('div');
				wrapperElement.classList.add('withdrawal-step');
				contextElement.appendChild(wrapperElement);
				wrapperElement.innerHTML = uploadDocument.html;
				viewReadyResolver(wrapperElement);
				return uploadDocument.viewDonePromise;
			})
			.then((kycDone) => {
				viewDoneResolver(kycDone);
			});
	});
}
function addActionButtonHandler(contextElement, sharedPaymentMethodStore, viewDoneResolver) {
	const actionButton = contextElement.ownerDocument.getElementById('flow-click-to-action');
	if (actionButton === null) {
		return;
	}

	const form = contextElement.ownerDocument.getElementById(getWithdrawFormId());
	if (form === null) {
		return;
	}

	actionButton.addEventListener('click', () => {
		const spinner = siblingSpinnerOnAction(event.target);

		const validationResult = formValidation.onSubmit(form);

		const url = form.action;
		const method = sharedPaymentMethodStore.getMethod();
		const account = sharedPaymentMethodStore.getAccount();

		const data = withdrawDataUsingAdapter(method, account);
		// add amount from form
		data.am = form.am.value;
		//setting isMobile true by default
		data.mobileDevice = true;


		if (validationResult.ok) {
			isKycNeeded({
				cid: account && account.id || -1,
				method: method.methodId,
				amount: data.am,
				currency: sharedPaymentMethodStore.getCurrency()
			})
				.then((kycResponse) => {
					if (kycResponse.needValidation) {
						spinnerOff(spinner);
						showKycPopup(kycResponse, contextElement, viewDoneResolver);
					} else {
						postJson(url, data)
							.then((response) => {
								/* method.send() does a full reload to show the result page
								 * /payment/result/withdraw, just like an external provider would.
								 *
								 * If this changes two things may be needed
								 *     1. spinnerOff
								 *     2. resolve viewDonePromise
								 */

								method.send(response);
							})
							.catch((e) => {
								spinnerOff(spinner);
								console.log(`Failed: ${url}`, data);
								const errorTitle = e.message.split(':')[0];
								const errorMessage = e.message.split(':')[1];
								console.log(`${errorTitle} ${errorMessage}`);
								notifications.showInformationMessage('fail', errorTitle, errorMessage, null, -1, null);
							});
					}
				});
		} else {
			spinnerOff(spinner);
		}
	});
}

function addFormHandler(contextElement, sharedPaymentMethodStore) {
	const formElement = contextElement.ownerDocument.getElementById(getWithdrawFormId());
	if (formElement) {
		const formHandler = amountFormChange.bind(null, sharedPaymentMethodStore, formElement);
		const delayHandler = validation.getDelayedFormInputHandler(formHandler);
		const focusInHandler = callbackOnFocusin.bind(formElement);
		formElement.addEventListener('input', delayHandler);
		formElement.addEventListener('focusin', focusInHandler);
	}
}

function addHandlers(contextElement, sharedPaymentMethodStore, viewDoneResolver) {
	addActionButtonHandler(contextElement, sharedPaymentMethodStore, viewDoneResolver);
	addFormHandler(contextElement, sharedPaymentMethodStore);

}

function viewReady(sharedPaymentMethodStore, viewDoneResolver, contextElement) {
	addHandlers(contextElement, sharedPaymentMethodStore, viewDoneResolver);
	const amountEl = contextElement.ownerDocument.getElementById(getWithdrawAmountFieldId());

	feeLimitUpdate(sharedPaymentMethodStore, amountEl.form)
		.then(() => {
			if (amountEl.value !== '') {
				formValidation.onInput(amountEl.form);
			}
		});

}

function withdrawAmountView(contextPath, sharedPaymentMethodStore, viewReadyPromise) {
	setViewIds(contextPath);

	let viewTaskDone;
	const viewDonePromise = new Promise(viewDoneResolver => {
		viewTaskDone = viewReady.bind(null, sharedPaymentMethodStore, viewDoneResolver);
	});
	viewReadyPromise.then(contextElement => {
		viewTaskDone(contextElement);
		return viewDonePromise;
	});

	const method = sharedPaymentMethodStore.getMethod();
	return fetchJson(`/template/withdraw/amount-form/${method.methodId}`)
		.then((restResult) => {
			const tpls = restResult.result.templates;
			const rootContext = {
				context: {
					path: contextPath
				},
				'withdraw-form-id': getWithdrawFormId(),
				'withdraw-amount-field-id': getWithdrawAmountFieldId(),
				'withdraw-fee-context-element': getFeeElementId(),
				'withdraw-limit-context-element': getLimitElementId(),
				'method-text': method.methodText,
				'method-id': method.methodId,
				'execute-url': method.executeUrl,
				i18n: restResult.result.rootContext.i18n,
				PARAM_AMOUNT: method.amount || sharedPaymentMethodStore.getLastUsedAmount(),
				SELECTED_CURRENCY: sharedPaymentMethodStore.getCurrency(),
			};

			Handlebars.registerHelper('currency-with-two-digits', (value, selectedCurrency) => {
				if (Number.isNaN(value) || !Number.isFinite(value)) {
					return '';
				}
				return currencyFormatter.format(value, selectedCurrency, getLocale());
			});

			Handlebars.registerPartial('fee', tpls.fee);
			Handlebars.registerPartial('withdraw-amount-form', tpls['withdraw-amount-form']);
			const amountTpl = Handlebars.compile(tpls['withdraw-amount']);
			amountTemplate.rootContext = rootContext;
			amountTemplate.template = Handlebars.compile(tpls['withdraw-amount-form']);
			amountTemplate.feeTemplate = Handlebars.compile(tpls.fee);
			return {
				title: restResult.result.rootContext.i18n.MOBILE_WITHDRAW_AMOUNT_PAGE_TITLE,
				html: amountTpl(rootContext),
				viewDonePromise
			};
		});
}

function uiFeedBackForMinMax(validationResult, formElement, amountMinLimit, amountMaxLimit) {
	const limitElement = formElement.ownerDocument.getElementById(getLimitElementId());
	if (validationResult.ok) {
		limitElement.innerHTML = '';
	} else {
		const element = validationResult.fail.am;
		if (element.dataset.isGreaterThanMax === 'true') {
			limitElement.innerHTML = amountTemplate.rootContext.i18n.MOBILE_WITHDRAW_FEELMT_SUMMARY_MAX_WITHDRAW + '&nbsp;' +
				currencyFormatter.format(amountMaxLimit, amountTemplate.rootContext.SELECTED_CURRENCY, getLocale());
		} else {
			limitElement.innerHTML = amountTemplate.rootContext.i18n.MOBILE_WITHDRAW_FEELMT_SUMMARY_MIN_WITHDRAW + '&nbsp;' +
				currencyFormatter.format(amountMinLimit, amountTemplate.rootContext.SELECTED_CURRENCY, getLocale());
		}
	}
}

function callbackOnFocusin(event) {
	const amountEl = event.target;
	amountEl.value = '';
	amountEl.placeholder = '';
}

export {
	getWithdrawFormId,
	getWithdrawAmountFieldId,
	withdrawAmountView
};



// WEBPACK FOOTER //
// ./mobile-core/src/payment/withdraw/withdraw-amount.js